import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import Inicio from 'Component/Admin/Inicio';
import AdminLayoutDefault from 'Component/Admin/Layout';
import LayoutDefault, { LayoutLogin } from 'Component/Layout';
import CategoriasAdminDefault, {
  CategoriasAdicionar as CategoriasAdminAdicionar,
  CategoriasEditar as CategoriasAdminEditar,
} from 'Screen/Admin/Categorias';
import CertificadosDefault from 'Screen/Admin/Certificados';
import CursosDefault, {
  CursosAdicionar,
  CursosEditar,
} from 'Screen/Admin/Cursos';
import DashboardsDefault from 'Screen/Admin/Dashboards';
import PlanosDefault, {
  PlanosAdicionar,
  PlanosEditar,
} from 'Screen/Admin/Planos';
import SolicitacoesAdminDefault, {
  SolicitacoesVisualizar as SolicitacoesAdminVisualizar,
} from 'Screen/Admin/Solicitacoes';
import TarefasDefault, {
  TarefasEditar,
  TarefasObservacoes,
  TarefasAlterarTitulo,
} from 'Screen/Admin/Tarefas';
import UnidadesDeAprendizagemDefault, {
  UnidadesDeAprendizagemAdicionar,
  UnidadesDeAprendizagemEditar,
} from 'Screen/Admin/UnidadesDeAprendizagem';
import UsuariosDefault, {
  UsuariosAdicionar as UsuariosAdminAdicionar,
  UsuariosAlterarMinhaSenha as UsuariosAdminAlterarMinhaSenha,
  UsuariosEditar,
} from 'Screen/Admin/Usuarios';
import AssinaturasDefault, {
  AssinaturasAdicionar,
  AssinaturasVisualizar,
} from 'Screen/Assinaturas';
import { AssistirMaisTardeDefault } from 'Screen/AssistirMaisTarde';
import { AulasAssistir, AulasProcessar } from 'Screen/Aulas';
import { AvaliacoesAdicionar, AvaliacoesProcessar } from 'Screen/Avaliacoes';
import CategoriasDefault, { CategoriasVisualizar } from 'Screen/Categorias';
import { CertificadosAdicionar } from 'Screen/Certificados';
import {
  CursosBuscar,
  CursosMeusEstudos,
  CursosVisualizar,
} from 'Screen/Cursos';
import FixasDefault, { FixasHome, FixasIntro } from 'Screen/Fixas';
import SolicitacoesDefault, {
  SolicitacoesAdicionar,
  SolicitacoesVisualizar,
} from 'Screen/Solicitacoes';
import {
  UsuariosAlterarMinhaSenha,
  UsuariosLogin,
  UsuariosPerfil
} from 'Screen/Usuarios';
import { useAppSelector } from 'Store';

interface Props {
  children: JSX.Element;
}

const Privado = ({ children }: Props): JSX.Element => {
  // Recupera o local atual em que o usuário se encontra
  const localizacao = useLocation();
  // Recupera os dados do usuário e token
  const token = useAppSelector((estado) => estado.autenticacao.token);

  // Variável que define se o usuário tem permissão
  // ou não para acessar o conteúdo privado
  let permitido = false;

  // Verifica se existe um token
  if (token) {
    permitido = true;
    // Separa as partes do token e pega seus dados
    const dados = JSON.parse(atob(token.split('.')[1]));
    // Recupera a data de expiração
    const expiracao = DateTime.fromSeconds(dados.exp);
    // Verifica se a data de expiração é maior que agora e permite o acesso
    if (expiracao > DateTime.now()) permitido = true;
  }

  // Se o usuário não estiver permitido, redireciona
  if (!permitido) {
    return <Navigate to="/login" state={{ from: localizacao }} replace />;
  }

  return children;
};

const Rota = () : React.ReactElement => {
  const localizacao = useLocation();

  useEffect(() => {
    ReactGA.send('pageview');
    ReactPixel.pageView();
  }, [ localizacao ]);

  return (
    <Routes location={ localizacao }>
      <Route element={ <LayoutLogin /> }>
        <Route index element={ <FixasDefault /> } />
        <Route path="login" element={ <UsuariosLogin /> } />
        {/* <Route path="cadastrar" element={ <UsuariosAdicionar /> } /> */}
      </Route>
      <Route path="intro" element={ <FixasIntro /> } />
      <Route path="estudar" element={ <Privado><LayoutDefault /></Privado> }>
        <Route index element={ <FixasHome /> } />
        <Route path="home" element={ <FixasHome /> } />
        <Route path="solicitacoes" element={ <SolicitacoesDefault /> } />
        <Route path="solicitacoes/adicionar" element={ <SolicitacoesAdicionar /> } />
        <Route path="solicitacoes/:id" element={ <SolicitacoesVisualizar /> } />
        <Route path="assinar" element={ <AssinaturasDefault /> } />
        <Route path="assinar/passar-cartao" element={ <AssinaturasAdicionar /> } />
        <Route path="minha-assinatura" element={ <AssinaturasVisualizar /> } />
        <Route path="curso/:slug" element={ <CursosVisualizar /> } />
        <Route path="curso/:slug/aula" element={ <AulasProcessar /> } />
        <Route path="curso/:slug/aula/:id" element={ <AulasProcessar /> } />
        <Route path="curso/:slug/aula/:id/assistir" element={ <AulasAssistir /> } />
        <Route path="curso/:slug/avaliacao/:id" element={ <AvaliacoesAdicionar /> } />
        <Route path="curso/:slug/avaliacao/processar" element={ <AvaliacoesProcessar /> } />
        <Route path="curso/:slug/certificado" element={ <CertificadosAdicionar /> } />
        <Route path="curso/:slug/certificado/:formato" element={ <CertificadosAdicionar /> } />
        <Route path="buscar" element={ <CursosBuscar /> } />
        <Route path="categorias" element={ <CategoriasDefault /> } />
        <Route path="categoria/:slug" element={ <CategoriasVisualizar /> } />
        <Route path="meus-estudos" element={ <CursosMeusEstudos /> } />
        <Route path="assistir-mais-tarde" element={ <AssistirMaisTardeDefault /> } />
        <Route path="alterar-senha" element={ <UsuariosAlterarMinhaSenha /> } />
        <Route path="perfil" element={ <UsuariosPerfil /> } />
      </Route>
      <Route path="admin" element={ <Privado><AdminLayoutDefault /></Privado> }>
        <Route index element={ <UsuariosDefault /> } />
        <Route path="inicio" element={ <Inicio /> } />
        <Route path="dashboard" element={ <DashboardsDefault /> } />
        <Route path="usuarios" element={ <UsuariosDefault /> } />
        <Route path="usuarios/adicionar" element={ <UsuariosAdminAdicionar /> } />
        <Route path="usuarios/editar/:id" element={ <UsuariosEditar /> } />
        <Route path="alterar-senha" element={ <UsuariosAdminAlterarMinhaSenha /> } />
        <Route path="categorias" element={ <CategoriasAdminDefault /> } />
        <Route path="categorias/adicionar" element={ <CategoriasAdminAdicionar /> } />
        <Route path="categorias/editar/:id" element={ <CategoriasAdminEditar /> } />
        <Route path="cursos" element={ <CursosDefault /> } />
        <Route path="cursos/adicionar" element={ <CursosAdicionar /> } />
        <Route path="cursos/editar/:id" element={ <CursosEditar /> } />
        <Route path="unidades-de-aprendizagem" element={ <UnidadesDeAprendizagemDefault /> } />
        <Route path="unidades-de-aprendizagem/adicionar" element={ <UnidadesDeAprendizagemAdicionar /> } />
        <Route path="unidades-de-aprendizagem/editar/:id" element={ <UnidadesDeAprendizagemEditar /> } />
        <Route path="certificados" element={ <CertificadosDefault /> } />
        <Route path="planos" element={ <PlanosDefault /> } />
        <Route path="planos/adicionar" element={ <PlanosAdicionar /> } />
        <Route path="planos/editar/:id" element={ <PlanosEditar /> } />
        <Route path="solicitacoes" element={ <SolicitacoesAdminDefault /> } />
        <Route path="solicitacoes/visualizar/:id" element={ <SolicitacoesAdminVisualizar /> } />
        <Route path="tarefas" element={ <TarefasDefault /> } />
        <Route path="tarefas/editar/:id" element={ <TarefasEditar /> } />
        <Route path="tarefas/alterar-titulo/:id" element={ <TarefasAlterarTitulo /> } />
        <Route path="tarefas/editar/:id/observacoes" element={ <TarefasObservacoes /> } />
      </Route>
    </Routes>
  )
}

export default Rota;
